import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaceSmileIcon,
  FingerPrintIcon,
  BellIcon,
  AcademicCapIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

interface GetStartedItem {
  title: string;
  to: string;
  description: string;
  icon: React.ForwardRefExoticComponent<any>;
  background: string;
  step?: number;
}

const items: GetStartedItem[] = [
  {
    title: 'Create your neoface',
    to: '/create-neoface',
    description: 'First, create a neoface that will represent your anonymous identity online.',
    icon: FingerPrintIcon,
    background: 'bg-pink-500',
    step: 1
  },
  {
    title: 'Anonymize your content',
    to: '/anonymize-content',
    description: 'Transform your photos and videos by applying your neoface to them.',
    icon: FaceSmileIcon,
    background: 'bg-green-500',
    step: 2
  },
  {
    title: 'Enable notifications',
    to: '/settings',
    description: 'Get notified when your content is ready for download.',
    icon: BellIcon,
    background: 'bg-amber-500',
  },
  {
    title: 'Learn best practices',
    to: '/learn',
    description: 'Master lighting, angles, and composition for professional-looking results.',
    icon: AcademicCapIcon,
    background: 'bg-blue-500',
  },
];

export default function GetStarted() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-slate-900">Get Started</h2>
        <p className="mt-1 text-sm text-slate-500">Complete these steps to start creating anonymous content</p>

        <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
          {items.map((item, index) => (
            <li key={index}>
              <Link
                to={item.to}
                className="group relative flex items-start space-x-4 rounded-lg p-4 hover:bg-slate-50 transition-colors"
              >
                <div
                  className={`${item.background} flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105`}
                >
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-slate-900">
                      {item.title}
                    </h3>
                    {item.step && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 flex-shrink-0">
                        Step {item.step}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-500 line-clamp-2">
                    {item.description}
                  </p>
                </div>

                <ChevronRightIcon
                  className="h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-colors"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
